import { CAPA_WORKFLOW_TYPES } from '@retracedgmbh/modules/capa/contracts';
import type { AuditStageId } from '@/lib/utils/auditStages';

// Ordered plan: each stage → the workflow step keys it represents. A stage only
// renders when at least one of its keys is present in `item.steps`, so a no-CAP
// audit never shows a CAP row, and Findings only appears after "Get AI
// suggestions" appends its steps.
//
// `reviewable` = the stage is an independent review entry point: finished →
// amber REVIEW row until its member steps carry `reviewedAt`, then grey
// COMPLETED. `capExtraction` IS reviewable on its own — CAP extraction is an
// independently-tracked review job (per product: flow-2 has audit-details +
// CAP-extraction as two review jobs), so a finished-but-unreviewed CAP stage
// shows its own Review row and flips to Completed once reviewed.
export const STAGE_PLAN: {
  id: AuditStageId;
  keys: string[];
  reviewable: boolean;
}[] = [
  {
    id: 'auditDetails',
    keys: [
      CAPA_WORKFLOW_TYPES.CAPA_CLASSIFICATION,
      CAPA_WORKFLOW_TYPES.EXTRACTION,
    ],
    reviewable: true,
  },
  {
    id: 'capExtraction',
    keys: [CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION],
    reviewable: true,
  },
  {
    id: 'findings',
    keys: [
      CAPA_WORKFLOW_TYPES.EXTRACT_FINDINGS,
      CAPA_WORKFLOW_TYPES.CAPS_CREATION,
    ],
    reviewable: true,
  },
];

// Plan for a CAP-ONLY workflow (`POST /capa/process` with just a `capFileId` —
// the CAP-import flow). It runs EXTRACTION + CAPA_CAP_FILE_DATA_EXTRACTION and
// NOTHING else: no classification, no findings. There is no audit to review, so
// the card must not render an "Audit details" row keyed off the raw EXTRACTION
// step (that row would be a permanent dead end — its review step doesn't exist
// in this flow). Instead the whole workflow is ONE user-facing stage, "CAP
// extraction", grouping both steps — the single review job the CAP-import
// resume lands on.
export const CAP_ONLY_STAGE_PLAN: {
  id: AuditStageId;
  keys: string[];
  reviewable: boolean;
}[] = [
  {
    id: 'capExtraction',
    keys: [
      CAPA_WORKFLOW_TYPES.EXTRACTION,
      CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION,
    ],
    reviewable: true,
  },
];

// Static per-step-key → i18n key map for the human-readable label of a single
// backend workflow step (finer-grained than the grouped `stage.*` labels). Keys
// are CAPA_WORKFLOW_TYPES values; values are static i18n keys — no dynamic
// `step.${key}` lookups, per the no-dynamic-translation-keys convention.
export const STEP_LABEL_KEYS: Record<string, string> = {
  [CAPA_WORKFLOW_TYPES.CAPA_CLASSIFICATION]: 'step.classification',
  [CAPA_WORKFLOW_TYPES.EXTRACTION]: 'step.extraction',
  [CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION]:
    'step.capFileDataExtraction',
  [CAPA_WORKFLOW_TYPES.EXTRACT_FINDINGS]: 'step.extractFindings',
  [CAPA_WORKFLOW_TYPES.CAPS_CREATION]: 'step.capsCreation',
};

// Steps NOT counted as user-facing jobs. The raw-parse EXTRACTION step is
// internal: it has no live progress (jumps 0→100, no per-step bar) and is folded
// into the "Audit details" row, so it never counts as a job of its own.
export const NON_JOB_STEP_KEYS = new Set<string>([
  CAPA_WORKFLOW_TYPES.EXTRACTION,
]);
